import { jsonataFactory, getJsonAta } from '@/utils';
import _ from 'lodash';
import NP from 'number-precision';
import { MetaOfRule, Rule, Values, RuleMap, PageRule } from './type';
import { RelationTreeType } from '@/asset/AssetTask/templates/Cascade/type';


// 基于字符串解析出逻辑实体code，并且确定该模型为头还是行
const getEntityType = (exp: string, values: Values) => {
  const entityCodes = ((exp || '').match(/([a-zA-Z0-9_]+)\./g) || []).map(x => x.replace('.', ''));
  if (entityCodes.length === 0) {
    entityCodes.push(exp);
  }
  const types = entityCodes.map(x => {
    if (_.isArray(values[x])) {
      return 'row';
    }
    if (_.isObject(values[x])) {
      return 'head';
    }
    return 'other';
  });
  if (types.filter((t) => t === 'head').length === entityCodes.length) {
    return 'head';
  }
  if (types.filter((t) => t === 'row').length === entityCodes.length) {
    return 'row';
  }
  if (types.includes('head') && types.includes('row')) {
    return 'mix';
  }
  return 'other';
};


function getRuleRes(rule: string, values: any) {
  try {
    const expression = getJsonAta(rule);
    let res = expression.evaluate(values);
    if (typeof res === 'number') {
      res = NP.strip(res);
    }
    return res;
  } catch (e) {
    console.warn('JsonAta evaluate error', e);
  }
}

/**
 * 获取树结构深度
 * @param code 模型code
 * @param relationTree
 * @returns
 */
function getDepth(code: string, relationTree: RelationTreeType[]): RelationTreeType | null {
  let stack = [...relationTree];
  while (stack.length > 0) {
    const next = stack.pop();
    if (next?.code === code) {
      return next;
    }
    if (next?.children) {
      stack = [...stack, ...next.children];
    }
  }
  return null;
}

/**
 * 基于行数据，生成1:1:n的计算数据
 * @param entityCode 模型code
 * @param entityData 模型行数据
 * @param values store全量数据
 * @param relationTree 模型关系树
 */
export function getValuesByRow(entityCode: string, entityData: any, values?: Values, relationTree?: RelationTreeType[]) {
  const rowAndHeadData = { ..._.omit(values, [entityCode]) };
  if (!relationTree) {
    return { ...rowAndHeadData, [entityCode]: entityData };
  }
  const entity = getDepth(entityCode, relationTree);
  // 当前行为第三个n
  if (entity && entity.floor === 3 && entityData?._headId) {
    const { parentCode } = entity;
    const parentRow = _.find(values?.[parentCode], x => x._id === entityData?._headId);
    return {
      ...rowAndHeadData,
      [parentCode]: parentRow,
      [entityCode]: entityData,
    };
  }
  return { ...rowAndHeadData, [entityCode]: entityData };
}

// 执行规则 values会被变更
export default function main(values: Values, pageRules: PageRule[], myRoles: any[], relationTree?: RelationTreeType[], rowIndex?: number): {
  values: Values;
  meta: MetaOfRule;
} {
  const rules = [...pageRules];
  // 将带有sum的计算排到最后
  // rules.sort((a, b) => {
  //   const expression = (a.TODOOPTIONS?.calculate || []).map(x => x.expression).join('');
  //   if (expression.includes('$sum')) {
  //     return 1;
  //   }
  //   return 0;
  // });

  // 经过规则处理后的meta信息
  const meta: MetaOfRule = {};

  const getModel = (modelCode: string) => {
    if (!meta[modelCode]) {
      meta[modelCode] = { fields: [] };
    }
    return meta[modelCode];
  };

  const getField = (modelCode: string, dataIndex: string) => {
    let filed = getModel(modelCode).fields.find((f) => f.dataIndex === dataIndex);
    if (!filed) {
      filed = { dataIndex };
      meta[modelCode].fields.push(filed);
    }
    return filed;
  };

  // 针对模型和字段进行计算处理
  const calculateAndSet = (rule: PageRule) => {
    rule.entitys?.forEach((entity) => {
      entity.attrs?.forEach((attr) => {
        const { calculate } = attr;
        if (!calculate) {
          return;
        }
        // 当前需要变更的数据类型
        const changeType = getEntityType(entity.code, values);
        // 需要变更的数据为头的，直接设置
        if (changeType === 'head') {
          const res = getRuleRes(calculate, { ...values, MYROLES: myRoles });
          (res !== undefined) && _.set(values, [entity.code, attr.code], res);
        }
        // 需要变更数据为行数据
        if (changeType === 'row') {
          // 计算依赖的数据
          const calculateType = getEntityType(calculate, values);
          (values[entity.code] as [] || []).forEach((rowData) => {
            let v;
            if (calculateType === 'head' || calculateType === 'mix' || calculateType === 'other') {
              v = getRuleRes(calculate, { ...values, MYROLES: myRoles });
            } else if (calculateType === 'row') {
              const rowAndHeadData = { ..._.omit(values, [entity.code]) };
              v = getRuleRes(calculate, { ...rowAndHeadData, [entity.code]: rowData, MYROLES: myRoles });
            }
            (v !== undefined) && _.set(rowData, attr.code, v);
          });
        }
      });
    });
  };
  // 规则执行
  rules.forEach((rule) => {
    const ruleType = getEntityType(rule.RULES, values);
    // 条件依赖头
    if (ruleType === 'head' || ruleType === 'mix' || ruleType === 'other') {
      const rulePass = getRuleRes(rule.RULES, { ...values, MYROLES: myRoles });
      if (!rulePass) {
        return;
      }
      // 变更所有模型状态
      rule.entitys?.forEach((entity) => {
        // 对模型和字段进行变更
        if (entity.visible === false) {
          const model = getModel(entity.code);
          model.visible = false;
        }
        entity.attrs?.forEach((attr) => {
          const filed = getField(entity.code, attr.code);
          filed.visible = attr.visible;
          filed.isRequired = attr.required;
          filed.isDisabled = attr.disabled;
          filed.bizComponent = attr.bizComponent;
        });
      });
      // 字段值计算
      calculateAndSet(rule);
    }
    // 条件依赖行
    if (ruleType === 'row') {
      // 基于全量执行一次 $sum(row.count) = 100
      rule.entitys?.forEach((entity) => {
        const changeType = getEntityType(entity.code, values);

        // 如果变更数据为头的，则每个字段计算一次
        if (changeType === 'head') {
          const rulePass = getRuleRes(rule.RULES, { ...values, MYROLES: myRoles });
          if (!rulePass) {
            return;
          }
          entity.attrs.forEach((attr) => {
            const { calculate } = attr;
            if (calculate) {
              const calculateRes = getRuleRes(calculate, { ...values, MYROLES: myRoles });
              (calculateRes !== undefined) && _.set(values, [entity.code, attr.code], calculateRes);
            }
          });
        }

        // 如果变更的数据为行字段，则每行计算
        if (changeType === 'row') {
          entity.attrs.forEach((attr) => {
            (values[entity.code] as [] || []).forEach((row, index) => {
              // 修改某一条时，单独出发当前行计算
              if (_.isNumber(rowIndex) && rowIndex !== index) {
                return;
              }
              // 生成计算的数据，1:n的需要生成为 {head: {}, row: {}}，1:n:n的需要转为 {hea1: {}, head2: {}, row: {}}
              const rulePass = getRuleRes(rule.RULES, { ...getValuesByRow(entity.code, row, values, relationTree), MYROLES: myRoles });
              if (!rulePass) {
                return;
              }
              if (attr.calculate) {
                const calculateRes = getRuleRes(attr.calculate, { ...getValuesByRow(entity.code, row, values, relationTree), MYROLES: myRoles });
                (calculateRes !== undefined) && _.set(row, [attr.code], calculateRes);
              }
            });
          });
        }
      });
    }
  });

  return {
    values,
    meta,
  };
}


/**
 * 对rulesMap 进行查询并计算
 * @param rulesMap 全局规则map，对应valuestroe中的rulesMap
 * @param id 对应map中的key
 * @param data 需要执行规则的数据，如果在行数据中，则需要传入rowData
 * @returns 当前id对应的可见、不可编辑、必填 状态
 */
export function ruleMapMatchExec(rulesMap: RuleMap, id: string, data: any, roles: any[]): {
  visible?: boolean;
  disabled?: boolean;
  required?: boolean;
} {
  const calcData = _.cloneDeep(data);
  calcData.MYROLES = roles;
  /** 是否可见处理 */
  const visibleExpression = _.get(rulesMap, [id!, 'visible']);
  const hiddenExpression = _.get(rulesMap, [id!, 'hidden']);
  let visible;
  if (visibleExpression) {
    visible = getJsonAta(visibleExpression).evaluate(calcData);
  } else if (hiddenExpression) {
    visible = !getJsonAta(hiddenExpression).evaluate(calcData);
  }

  /** 是否可编辑处理 */
  const disabledExpression = _.get(rulesMap, [id!, 'disabled']);
  let disabled;
  if (disabledExpression && _.isString(disabledExpression)) {
    disabled = getJsonAta(disabledExpression).evaluate(calcData);
  }
  if (disabledExpression && _.isArray(disabledExpression)) {
    disabled = disabledExpression.map(exp => getJsonAta(exp).evaluate(calcData)).includes(true);
  }

  /** 是否必填处理 */
  const requiredExpression = _.get(rulesMap, [id!, 'required']);
  let required;
  if (requiredExpression && _.isString(requiredExpression)) {
    required = getJsonAta(requiredExpression).evaluate(calcData);
  }
  if (requiredExpression && _.isArray(requiredExpression)) {
    required = requiredExpression.map(exp => getJsonAta(exp).evaluate(calcData)).includes(true);
  }

  const res = { visible, disabled, required };

  return Object.fromEntries(Object.entries(res).filter((item) => _.isBoolean(item[1])));
}
